import React, { useCallback, useEffect, useRef, useState } from "react"
import Head from "next/head"
import {
  addVideoFullScreenDatalayer,
  playVideoDatalayer,
} from "@/components/Default/Video/videoAnalytics"
import DOMPurify from "isomorphic-dompurify"
import useScreenOrientation from "@/hooks/useScreenOrientation"
import useDeferredVideo from "@/hooks/useDeferredVideo"
import useWindowDimensions from "@/hooks/useWindowDimensions"
import {
  addMediaToFavorites,
  checkIsFavorite,
  convertStringToBoolean,
  createIdFromString,
  getRandom,
  removeHtmlSuffixFromAnchors,
  removeFavorites,
  sanitizeInnerHtml,
} from "@/utils/helper"
import Action from "@/components/Default/Teaser/v1/Action"
import Description from "@/components/Default/Teaser/v1/Description"
import { useDispatch, useSelector } from "react-redux"
import { selectGenericState, showToast } from "@/store/features/genericSlice"
import { useInView } from "react-intersection-observer"
import { MIN_VIDEO_DURATION, VIDEO_EVENTS } from "@/constants/index"
import { getConfig } from "@/constants/config"
import {
  buildPosterSrcSet,
  onClickHeroVideo,
  onPlayVideoInModal,
  onShareVideo,
  onPlayVideo,
  onVideoInteraction,
  onVideoPopupClose,
} from "@/utils/videoHelper"
import SocialShare from "@/components/cart/OrderSummary/SocialShare"
import {
  PURE_DATA_FOR_STRING_REGEX,
  PURE_TEXT_REGEX,
  VIDEO_ID_REGEX,
} from "@/constants/regex"
import useSearchi18n from "@/i18n/useSearchi18n"
import {
  selectAuthState,
  setAuthModalVisibility,
} from "@/store/features/authSlice"
import { getFavorites } from "@/store/features/favoritesSlice"

const Video = props => {
  const {
    data: {
      videoTitle = "",
      videoMobile = "",
      videoMobileLandscape = "",
      videoMobilePoster = "",
      videoDesktopPoster = "",
      videoDesktop = "",
      muteEnableAutoPlay = "false",
      hideEnableAutoPlay = "false",
      appliedCssClassNames = "",
      items = [],
      teaserProperties: {
        title = "",
        description = "",
        linkURLRef = false,
        linkCTA = "",
        linkNewTab = false,
        videoPosterMobileLandscape = "",
        controlType = "",
        pageType = "",
        componentPosition = "",
        componentZone = "",
        componentLinkType = "",
        playsAboveFold = "false",
        eventName = "",
        dynamicTabName = "",
        eventAction = "",
        caption = "",
        pretitle = "",
        actionsEnabled = false,
        actions = [],
        linkAll = false,
        dataLayerLinkType = "",
        mainTitle = "",
        enableFav = false,
        enableShare = false,
        linkDescription = false,
        propertiesPageType = "",
      } = {},
    } = {},
  } = props
  const {
    eventPageType,
    toaster: { content },
  } = useSelector(selectGenericState)

  const { winWidth } = useWindowDimensions()
  const screenOrientation = useScreenOrientation()
  const [video, setVideo] = useState("")
  const [dynamicId, setDynamicId] = useState("")
  const [videoPlayed, setVideoPlayed] = useState(false)
  const [videoReady, setVideoReady] = useState(false)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)
  const [showSocialShare, setShowSocialShare] = useState(false)

  const { ref: inViewRef, inView } = useInView({
    threshold: 0.1,
    rootMargin: "200px 0px",
  })

  const videoContainerRef = useRef(null)
  const videoRef = useRef(null)
  const favEle = useRef(null)
  const [general, setGeneral] = useState(null)
  const [toasterContent, setToasterContent] = useState({
    successMessage: "",
    unfavoriteSuccessMessage: "",
  })
  const [showControls, setShowControls] = useState(false)
  const { isAuth } = useSelector(selectAuthState)
  const [isCallback, setIsCallback] = useState(false)
  const isTriggeredRef = useRef(true)
  const dispatch = useDispatch()

  const [flagState, setFlagState] = useState({
    flag25: false,
    flag75: false,
    flag50: false,
    flag100: false,
  })

  const getVideoId = video => {
    return decodeURI(video)?.split(VIDEO_ID_REGEX)[1]?.toLowerCase()
  }
  const videoId = getVideoId(video)
  useEffect(() => {
    getConfig().then(config => {
      setGeneral(config?.general)
    })
  }, [])
  const videoName = `${
    videoTitle.replace(PURE_DATA_FOR_STRING_REGEX, "") ||
    componentLinkType.toLowerCase().replace(PURE_DATA_FOR_STRING_REGEX, "") ||
    "hero video"
  }`

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (document.fullscreenElement === videoRef?.current) {
        addVideoFullScreenDatalayer(
          "fullscreenEnter",
          eventPageType,
          videoName,
          videoId,
          componentPosition
        )
      } else {
        addVideoFullScreenDatalayer(
          "fullscreenExit",
          eventPageType,
          videoName,
          videoId,
          componentPosition
        )
      }
    }
    document.addEventListener("fullscreenchange", handleFullscreenChange)
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange)
    }
  }, [eventPageType, videoId, videoName, componentPosition])

  useEffect(() => {
    if (videoTitle) {
      const dynamicId = createIdFromString(videoTitle)
      setDynamicId(dynamicId)
    }
  }, [videoTitle])

  useEffect(() => {
    if (content) {
      setToasterMessage()
    }
  }, [content])

  const onTimeUpdate = event => {
    const videoDuration = videoRef.current.duration
    const percent25 = Math.floor(videoDuration * 0.25)
    const percent50 = Math.floor(videoDuration * 0.5)
    const percent75 = Math.floor(videoDuration * 0.75)
    const percent100 = Math.floor(videoDuration)
    const progressTime = Math.floor(videoRef.current.currentTime)

    if (
      (muteEnableAutoPlay === "true" ? percent100 - 1 : percent100) ===
        progressTime &&
      flagState.flag100 === false &&
      !convertStringToBoolean(muteEnableAutoPlay)
    ) {
      setFlagState(prevState => ({
        ...prevState,
        flag100: true,
      }))
      addVideoDataLayer(100)
    } else if (
      percent75 <= progressTime &&
      flagState.flag75 === false &&
      !convertStringToBoolean(muteEnableAutoPlay)
    ) {
      setFlagState(prevState => ({
        ...prevState,
        flag75: true,
      }))
      addVideoDataLayer(75)
    } else if (
      percent50 <= progressTime &&
      flagState.flag50 === false &&
      !convertStringToBoolean(muteEnableAutoPlay)
    ) {
      setFlagState(prevState => ({
        ...prevState,
        flag50: true,
      }))
      addVideoDataLayer(50)
    } else if (
      percent25 <= progressTime &&
      flagState.flag25 === false &&
      !convertStringToBoolean(muteEnableAutoPlay)
    ) {
      setFlagState(prevState => ({
        ...prevState,
        flag25: true,
      }))

      addVideoDataLayer(25)
    }
  }
  const addVideoDataLayer = percentViewed => {
    const { VIDEO_VIEWED, VIDEO_COMPLETE } = VIDEO_EVENTS
    const eventInfo = {
      clickInternalLinks: "true",
      eventAction: `${eventPageType?.toLowerCase()}:${
        percentViewed === 100
          ? `${videoTitle}:${VIDEO_COMPLETE}`
          : `${percentViewed}% ${VIDEO_VIEWED}`
      }`,
      eventMsg: "n/a",
      eventName: `${eventPageType?.toLowerCase()}:${
        percentViewed === 100
          ? `${videoTitle}:${VIDEO_COMPLETE}`
          : `${percentViewed}% ${VIDEO_VIEWED}`
      }`,
      eventStatus: "n/a",
      eventType: "navigation",
      internalLinkName: `${
        percentViewed === 100
          ? VIDEO_COMPLETE
          : `${percentViewed}% ${VIDEO_VIEWED}`
      }`,
      internalLinkPosition: `${eventPageType?.toLowerCase()}:${
        componentPosition || "teaser"
      }`,
      internalLinkType: `${eventPageType?.toLowerCase()}:${
        percentViewed === 100 ? videoTitle : componentPosition || "teaser"
      }`,
      internalLinkURL: "n/a",
      internalLinkZoneName: `${eventPageType?.toLowerCase()}${
        percentViewed === 100 ? ":" + videoTitle : ""
      }`,
      videoId: videoId,
      videoName: videoName,
      videoType: `${
        muteEnableAutoPlay === "true" ? "auto play" : "user initiated"
      }`,
    }
    const page = window.pageObj || {}
    // const { productInfo: tempInfo } = page
    if (page?.pageInfo?.pageId === "home") {
      delete page.productInfo
    }

    window.adobeDataLayer.push({
      event: "cmp:click",
      page,
      eventInfo,
    })
  }

  const {
    favSuccessMsg = "",
    favErrorMsg = "",
    removeItem = "",
    video: videoLabel,
  } = useSearchi18n({})

  const getVideoAccessibleName = () => {
    const candidate = videoTitle || title || componentLinkType || caption || ""
    const normalized = String(candidate).replace(PURE_TEXT_REGEX, "").trim()
    return normalized || videoLabel
  }

  const videoAccessibleName = getVideoAccessibleName()

  const setToasterMessage = async () => {
    setToasterContent({
      successMessage: favSuccessMsg,
      unfavoriteSuccessMessage: removeItem,
    })
  }

  const getInitialFavoriteState = () => {
    dispatch(getFavorites())
      .unwrap()
      .then(response => {
        const { textLineItems = [] } = response
        checkIsFavorite(textLineItems)
        const id = favEle.current.getAttribute("data-id")
        if (id) {
          dispatch(
            showToast({
              message: favErrorMsg,
              isVisible: true,
            })
          )
        } else {
          onFavVideo({})
        }
      })
  }

  const onFavVideo = event => {
    event?.preventDefault && event?.preventDefault()
    event?.stopPropagation && event?.stopPropagation()
    const { successMessage = "", unfavoriteSuccessMessage = "" } =
      toasterContent
    if (!isAuth) {
      handleLogin(event)
      return
    }
    let _$$closest
    const id = event?.target?.getAttribute("data-id")
    const sharedZoneName =
      (_$$closest = $(void 0).closest(".cmp-container")) === null ||
      _$$closest === void 0
        ? void 0
        : _$$closest.attr("id")

    if (id) {
      removeFavorites([id], favEle, unfavoriteSuccessMessage)
    } else {
      const assetUrl = videoDesktop

      let _assetUrl
      const url =
        (_assetUrl = assetUrl) !== null &&
        _assetUrl !== void 0 &&
        _assetUrl.startsWith("/")
          ? window.location.origin + assetUrl
          : assetUrl
      addMediaToFavorites(
        "video",
        title,
        url,
        successMessage,
        "",
        caption,
        sharedZoneName,
        checkVideoTitle,
        favVideoId
      )
    }
  }

  useEffect(() => {
    if (!isTriggeredRef.current && isCallback && isAuth) {
      isTriggeredRef.current = true
      setIsCallback(false)
      getInitialFavoriteState()
    }
  }, [isCallback, isAuth, isTriggeredRef])

  const handleLogin = () => {
    setIsCallback(true)
    isTriggeredRef.current = false
    dispatch(setAuthModalVisibility({ show: true }))
  }

  const handleLoadedMetadata = () => {
    const duration = videoRef.current?.duration || 0
    // Enable controls if video is 5 seconds or longer
    if (duration >= MIN_VIDEO_DURATION) {
      setShowControls(true)
    } else {
      setShowControls(false)
    }
  }

  const setVideoSource = useCallback(() => {
    const isDesktop = window.matchMedia("(min-width: 1025px)").matches
    const isLandscape = window.matchMedia("(orientation: landscape)").matches

    const selectedVideo = isDesktop
      ? videoDesktop
      : isLandscape
      ? videoMobileLandscape
      : videoMobile

    if (video && video !== selectedVideo) setVideoReady(false)
    if (isDesktop) {
      setVideo(videoDesktop)
    } else if (isLandscape) {
      setVideo(videoMobileLandscape)
    } else {
      setVideo(videoMobile)
    }
  }, [
    video,
    videoMobile,
    videoMobileLandscape,
    videoDesktop,
    screenOrientation,
    winWidth,
  ])

  const isAboveFold = convertStringToBoolean(playsAboveFold)
  const unloadVideo = useCallback(() => {
    videoRef?.current?.pause()
    setVideo("")
    setVideoPlayed(false)
    setVideoReady(false)
    setIsVideoPlaying(false)
  }, [])
  const { autoPlayAllowed, clearPlayRequest, playRequested, requestVideoLoad } =
    useDeferredVideo({
      inView,
      isAboveFold,
      loadVideo: setVideoSource,
      unloadVideo,
    })
  const [modalPlayRequested, setModalPlayRequested] = useState(false)

  useEffect(() => {
    if (!inView) setModalPlayRequested(false)
  }, [inView])

  useEffect(() => {
    if (video && playRequested && videoRef.current) {
      const playPromise = videoRef.current.play()
      playPromise?.catch(() => {})
      clearPlayRequest()
    }
  }, [clearPlayRequest, playRequested, video])

  useEffect(() => {
    if (video && modalPlayRequested && videoRef.current) {
      setModalPlayRequested(false)
      onPlayVideoInModal(
        { preventDefault: () => {} },
        propertiesPageType,
        videoContainerRef?.current,
        videoRef?.current
      )
    }
  }, [modalPlayRequested, propertiesPageType, video])

  useEffect(() => {
    if (isVideoPlaying) setVideoPlayed(true)
  }, [isVideoPlaying])

  const eventActionTabName = dynamicTabName || "zoneForVideo"

  const eventActionName = `${dynamicTabName || "rm"}:video:rm`

  const posterLoading = isAboveFold ? "eager" : "lazy"
  const posterFetchPriority = isAboveFold ? "high" : "low"
  const preservePosterLcp =
    isAboveFold && convertStringToBoolean(muteEnableAutoPlay)
  const desktopPosterSrcSet = preservePosterLcp
    ? buildPosterSrcSet(videoDesktopPoster)
    : ""
  const landscapePosterSrcSet = preservePosterLcp
    ? buildPosterSrcSet(videoPosterMobileLandscape)
    : ""
  const mobilePosterSrcSet = preservePosterLcp
    ? buildPosterSrcSet(videoMobilePoster)
    : ""

  const handlePlayVideoInModal = event => {
    if (!video && propertiesPageType === "modalWindow") {
      event.preventDefault()
      setModalPlayRequested(true)
      requestVideoLoad()
      return
    }

    onPlayVideoInModal(
      event,
      propertiesPageType,
      videoContainerRef?.current,
      videoRef?.current
    )
  }

  const handlePlayVideo = () => {
    if (!video) {
      requestVideoLoad({ play: true })
      return
    }

    onPlayVideo(
      videoRef?.current,
      res => setIsVideoPlaying(res),
      videoDesktopPoster,
      videoMobilePoster,
      video,
      isVideoPlaying
    )
  }

  const [favVideoId, setFavVideoId] = useState("")

  const analyticEventData = {
    clickInternalLinks: true,
    internalLinkPosition: `${eventPageType?.toLowerCase()}:${
      componentPosition || "teaser"
    }`,
    internalLinkZoneName: `${eventPageType?.toLowerCase()}:${
      componentZone || "teaser"
    }`,
    internalLinkURL: "n/a",
    internalLinkName: "n/a",
    eventName: `${eventPageType?.toLowerCase()}:share`,
    internalLinkType: `${eventPageType?.toLowerCase()}:share`,
    eventType: "informative",
    eventAction: `${eventPageType?.toLowerCase()}:share`,
    videoName: `${
      videoTitle || componentLinkType.toLowerCase() || "hero video"
    }`,
    videoId: `${getVideoId(video)}`,
    eventMsg: "n/a",
    eventStatus: "n/a",
  }
  const checkVideoTitle =
    videoTitle || componentLinkType.toLowerCase() || "hero video"

  const sanitizedCaption = DOMPurify.sanitize(
    removeHtmlSuffixFromAnchors(caption)
  )

  return (
    <section className={`video teaser ${appliedCssClassNames}`} ref={inViewRef}>
      {isAboveFold ? (
        <Head>
          {videoDesktopPoster ? (
            <link
              rel="preload"
              as="image"
              href={videoDesktopPoster}
              media="(min-width: 1025px)"
              {...(desktopPosterSrcSet
                ? { imageSrcSet: desktopPosterSrcSet, imageSizes: "100vw" }
                : {})}
              {...{ fetchpriority: "high" }}
            />
          ) : null}
          {videoPosterMobileLandscape ? (
            <link
              rel="preload"
              as="image"
              href={videoPosterMobileLandscape}
              media="(min-width: 640px) and (max-width: 1024px)"
              {...(landscapePosterSrcSet
                ? { imageSrcSet: landscapePosterSrcSet, imageSizes: "100vw" }
                : {})}
              {...{ fetchpriority: "high" }}
            />
          ) : null}
          {videoMobilePoster ? (
            <link
              rel="preload"
              as="image"
              href={videoMobilePoster}
              media="(max-width: 639px)"
              {...(mobilePosterSrcSet
                ? { imageSrcSet: mobilePosterSrcSet, imageSizes: "100vw" }
                : {})}
              {...{ fetchpriority: "high" }}
            />
          ) : null}
        </Head>
      ) : null}
      <div
        className={`cmp-teaser${
          controlType === "customControl" ? " gbh-custom-control" : ""
        }${propertiesPageType === "modalWindow" ? " gbh-model-window" : ""}${
          convertStringToBoolean(hideEnableAutoPlay) ? " gbh-hide-mute" : ""
        }`}
        ref={videoContainerRef}
        aria-label={dynamicId ?? ""}
      >
        <div
          className={`cmp-teaser__image video_poster_height${
            videoPlayed ? " video-played" : ""
          }${videoReady ? " video-ready" : ""}`}
          data-gbh-data-layer={`{"clickInternalLinks":"true","internalLinkPosition":"${eventPageType?.toLowerCase()}:${
            componentPosition || "teaser"
          }","internalLinkZoneName":"${eventPageType?.toLowerCase()}:'hero banner' ","internalLinkURL":"n/a","internalLinkName":"video play","eventName":"${eventPageType?.toLowerCase()}:${eventActionTabName}",
             "internalLinkType":"${eventPageType?.toLowerCase()}:${
            videoTitle
              ? videoTitle
              : componentLinkType.toLowerCase() || "teaser"
          }","eventType":"navigation","eventAction":"${eventPageType?.toLowerCase()}:${eventActionTabName}","videoName":"${
            videoTitle || componentLinkType.toLowerCase() || "hero video"
          }","videoId":"${videoId}","videoType": "${
            muteEnableAutoPlay === "true" ? "auto play" : "user initiated"
          }"}`}
          style={showSocialShare ? { overflow: "visible" } : {}}
        >
          <span
            className={`gbh-play-video desktop${
              isVideoPlaying ? " active" : ""
            }`}
            onClick={handlePlayVideoInModal}
          >
            <button
              className="playinside desktop"
              id={`videoinnerbtn-${dynamicId}`}
              aria-label={isVideoPlaying ? "pause video" : "play video"}
              aria-pressed={isVideoPlaying}
              title={isVideoPlaying ? "Pause video" : "Play video"}
              onClick={handlePlayVideo}
            />
          </span>

          {preservePosterLcp || !videoPlayed ? (
            <picture
              className={`poster-img${
                preservePosterLcp ? " gbh-poster-lcp" : ""
              }`}
            >
              <source
                media="(min-width:1025px)"
                srcSet={desktopPosterSrcSet || `${videoDesktopPoster}`}
                sizes="100vw"
              />
              <source
                media="(min-width:640px) and (max-width:1024px)"
                srcSet={
                  landscapePosterSrcSet || `${videoPosterMobileLandscape}`
                }
                sizes="100vw"
              />
              <source
                media="(max-width:639px)"
                srcSet={mobilePosterSrcSet || `${videoMobilePoster}`}
                sizes="100vw"
              />
              <img
                src={`${videoDesktopPoster}`}
                alt=""
                className="desktop-poster"
                loading={posterLoading}
                {...{ fetchpriority: posterFetchPriority }}
              />
            </picture>
          ) : null}

          {video && (
            <>
              <video
                ref={videoRef}
                id={`video-desktop-${dynamicId}`}
                title={videoAccessibleName}
                aria-label={videoAccessibleName}
                width="100%"
                controls={
                  showControls &&
                  !convertStringToBoolean(hideEnableAutoPlay) &&
                  (videoPlayed || isVideoPlaying)
                }
                muted={convertStringToBoolean(muteEnableAutoPlay)}
                autoPlay={
                  autoPlayAllowed && convertStringToBoolean(muteEnableAutoPlay)
                }
                loop={convertStringToBoolean(muteEnableAutoPlay)}
                playsInline
                preload={isAboveFold ? "metadata" : "none"}
                onLoadedMetadata={handleLoadedMetadata}
                onLoadedData={() => {
                  if (convertStringToBoolean(muteEnableAutoPlay)) {
                    playVideoDatalayer(videoRef?.current, video, id =>
                      setFavVideoId(id)
                    )
                  }
                }}
                className={`hero-video videoelement${
                  convertStringToBoolean(muteEnableAutoPlay) ? " muted" : ""
                }${
                  convertStringToBoolean(playsAboveFold) ? "" : " lazy-video"
                }${preservePosterLcp ? " gbh-preserve-poster-lcp" : ""}`}
                onPlaying={() => setVideoReady(true)}
                onClick={event => onClickHeroVideo(event, videoRef?.current)}
                onPlay={() =>
                  onVideoInteraction(
                    VIDEO_EVENTS.PLAY,
                    () => setVideoPlayed(true),
                    res => setIsVideoPlaying(res),
                    videoRef?.current,
                    video,
                    convertStringToBoolean(muteEnableAutoPlay),
                    id => setFavVideoId(id),
                    flagState
                  )
                }
                onPause={() =>
                  onVideoInteraction(
                    VIDEO_EVENTS.PAUSE,
                    () => setVideoPlayed(true),
                    res => setIsVideoPlaying(res),
                    videoRef?.current,
                    video,
                    convertStringToBoolean(muteEnableAutoPlay),
                    flagState
                  )
                }
                onEnded={() =>
                  onVideoInteraction(
                    VIDEO_EVENTS.ENDED,
                    () => setVideoPlayed(true),
                    res => setIsVideoPlaying(res),
                    videoRef?.current,
                    video,
                    convertStringToBoolean(muteEnableAutoPlay),
                    flagState
                  )
                }
                onTimeUpdate={onTimeUpdate}
              >
                <source src={video} type="video/mp4" />
                {items.map((transcript, index) => (
                  <track
                    key={`video-track-${dynamicId}-${index}`}
                    label={transcript?.languageLabel}
                    kind="captions"
                    srcLang={transcript?.languageCode}
                    src={transcript?.transcriptSrcPath}
                    default={items?.length ? true : false}
                  />
                ))}
              </video>
            </>
          )}

          <div
            className="cmp-img-caption"
            data-gbh-data-layer={`{"clickInternalLinks":"true","internalLinkPosition":"${eventPageType?.toLowerCase()}:${
              componentPosition || "teaser"
            }","internalLinkZoneName":"${eventPageType?.toLowerCase()}:${
              componentZone || "teaser"
            }","internalLinkURL":"n/a","internalLinkName":"n/a","eventName":"${eventPageType?.toLowerCase()}:${
              eventName || eventActionName
            }","internalLinkType":"${eventPageType?.toLowerCase()}:${
              videoTitle || componentLinkType || "teaser"
            }","eventType":"navigation","eventAction":"${eventPageType?.toLowerCase()}:${
              eventAction || eventActionName
            }"}`}
            dangerouslySetInnerHTML={{
              __html: sanitizedCaption,
            }}
          />
          <div
            className="vidio_popup_close_button"
            onClick={() =>
              onVideoPopupClose(
                videoContainerRef?.current,
                videoRef?.current,
                video
              )
            }
          >
            <button aria-label="close video player" title="Close video player">
              <span aria-hidden="true">×</span>
            </button>
          </div>
        </div>

        <div className="cmp-teaser__content">
          {pretitle && (
            <div className="cmp-teaser__pretitle">
              {sanitizeInnerHtml(pretitle)}
            </div>
          )}
          {title && (
            <div className="cmp-teaser__title">{sanitizeInnerHtml(title)}</div>
          )}
          {description && (
            <Description
              general={general}
              linkURL={linkCTA}
              linkURLRef={linkURLRef}
              linkNewTab={linkNewTab}
              description={description}
              mainTitle={mainTitle}
              componentPosition={componentPosition}
              componentLinkType={componentLinkType}
              pageType={pageType}
              linkDescription={linkDescription}
            />
          )}
          {actionsEnabled && (
            <div className="cmp-teaser__action-container">
              {actions.map((action, index) => {
                const {
                  playVideoCTA = false,
                  primaryCTA = false,
                  secondaryCTA = false,
                  url = "",
                  title = "",
                  newTab = "",
                  urlRef = "",
                } = action
                return (
                  <Action
                    key={`video-action-${index + 1}-${getRandom()}`}
                    primaryCTA={convertStringToBoolean(primaryCTA)}
                    secondaryCTA={convertStringToBoolean(secondaryCTA)}
                    playVideoCTA={convertStringToBoolean(playVideoCTA)}
                    link={url}
                    text={title}
                    newTab={convertStringToBoolean(newTab)}
                    urlRef={urlRef}
                    id={dynamicId}
                    pageType={pageType}
                    videoTitle={videoTitle}
                    linkAll={convertStringToBoolean(linkAll)}
                    mainTitle={mainTitle}
                    dataLayerLinkType={dataLayerLinkType}
                    eventName={eventName}
                    eventAction={eventAction}
                    componentPosition={componentPosition}
                    propertiesPageType={propertiesPageType}
                    playVideoCTAcb={handlePlayVideoInModal}
                  />
                )
              })}
            </div>
          )}
          {/* Enable favorite and share button on video */}
          {(enableFav || enableShare) && (
            <div className="cmp-teaser__icons">
              {enableFav && (
                <button
                  ref={favEle}
                  className="favorite fav-video-trigger"
                  data-asset={videoDesktop}
                  data-url={videoDesktop}
                  aria-label="add video to favorites"
                  title="Add video to favorites"
                  onClick={event => onFavVideo(event)}
                />
              )}
              {enableShare && (
                <button
                  className="share share-video-trigger"
                  aria-label={
                    showSocialShare ? "close share options" : "share video"
                  }
                  aria-expanded={showSocialShare}
                  title="Share video on social media"
                  onClick={e =>
                    onShareVideo(
                      e,
                      () => setShowSocialShare(!showSocialShare),
                      eventPageType,
                      componentZone,
                      componentLinkType,
                      videoTitle
                    )
                  }
                >
                  {showSocialShare && (
                    <div
                      className="share-cmp"
                      style={{ display: "block" }}
                      role="region"
                      aria-label="Share options"
                    >
                      <SocialShare
                        analyticEventData={analyticEventData}
                        componentProp={{ assetUrl: videoDesktop }}
                        enableDownload={false}
                        videoComp={true}
                      />
                    </div>
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default React.memo(Video)
